import React, { useState, useEffect } from 'react';
import { Bell, Calendar, StickyNote, RefreshCw } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useUpdateReminder } from '../../hooks/useReminders';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';
import { normalizeFieldErrors } from '../../services/api';


const EditReminderModal = ({ isOpen, onClose, reminder }) => {
    const [formData, setFormData] = useState({
        title: '',
        due_date: '',
        frequency: 'monthly',
        description: '',
        is_active: true,
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const updateReminder = useUpdateReminder();
    const { t } = useLanguage();

    const frequencyOptions = [
        { value: 'once', label: t(KEYS.REMINDER_FREQ_ONCE) },
        { value: 'daily', label: t(KEYS.REMINDER_FREQ_DAILY) },
        { value: 'weekly', label: t(KEYS.REMINDER_FREQ_WEEKLY) },
        { value: 'monthly', label: t(KEYS.REMINDER_FREQ_MONTHLY) },
        { value: 'quarterly', label: t(KEYS.REMINDER_FREQ_QUARTERLY) },
        { value: 'yearly', label: t(KEYS.REMINDER_FREQ_YEARLY) },
    ];

    useEffect(() => {
        if (reminder) {
            setFormData({
                title: reminder.title || '',
                due_date: reminder.due_date ? reminder.due_date.split('T')[0] : '',
                frequency: reminder.frequency || 'monthly',
                description: reminder.description || '',
                is_active: reminder.is_active ?? true,
            });
        }
    }, [reminder]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = t(KEYS.REMINDER_FIELD_TITLE_REQUIRED);
        } else if (formData.title.length > 191) {
            newErrors.title = t(KEYS.COMMON_ERROR_MAX_CHARS);
        }

        if (!formData.due_date) {
            newErrors.due_date = t(KEYS.REMINDER_FIELD_DATE_REQUIRED);
        }

        if (!formData.frequency) {
            newErrors.frequency = t(KEYS.REMINDER_FIELD_FREQ_REQUIRED);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            const data = {
                title: formData.title.trim(),
                due_date: formData.due_date,
                frequency: formData.frequency,
                is_active: formData.is_active,
                description: formData.description.trim() || undefined,
            };

            await updateReminder.mutateAsync({ reminderUid: reminder.uid, data });
            onClose();
        } catch (error) {
            if (error.code === 'VALIDATION_ERROR' && error.errors) {
                setErrors(normalizeFieldErrors(error.errors));
            } else {
                setSubmitError(error.message || t(KEYS.REMINDER_EDIT_ERROR));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setErrors({});
            setSubmitError('');
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.REMINDER_EDIT_TITLE)}
            subtitle="Update reminder details"
            size="medium"
            footer={
                <>
                    <Button variant="outline" size="medium" onClick={handleClose} disabled={isSubmitting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="primary" size="medium" onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? t(KEYS.REMINDER_EDIT_SUBMITTING) : t(KEYS.REMINDER_EDIT_SUBMIT)}
                    </Button>
                </>
            }
        >
            {submitError && (
                <div className="p-3 bg-status-overdue-bg text-status-overdue rounded text-sm mb-4">
                    {submitError}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
                {/* Title */}
                <div className="space-y-1">
                    <label htmlFor="edit-title" className="label-base flex items-center gap-1.5">
                        <Bell className="w-4 h-4" />
                        Title <span className="text-status-overdue">*</span>
                    </label>
                    <input
                        type="text"
                        id="edit-title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        className={`input-base ${errors.title ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="e.g., Pay electricity bill"
                        maxLength={191}
                        disabled={isSubmitting}
                    />
                    {errors.title && (
                        <span className="text-xs text-red-500 mt-1">{errors.title}</span>
                    )}
                </div>

                {/* Due Date */}
                <div className="space-y-1">
                    <label htmlFor="edit-due_date" className="label-base flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        Due Date <span className="text-status-overdue">*</span>
                    </label>
                    <input
                        type="date"
                        id="edit-due_date"
                        name="due_date"
                        value={formData.due_date}
                        onChange={handleChange}
                        className={`input-base ${errors.due_date ? 'border-red-500 focus:ring-red-500' : ''}`}
                        disabled={isSubmitting}
                    />
                    {errors.due_date && (
                        <span className="text-xs text-red-500 mt-1">{errors.due_date}</span>
                    )}
                </div>

                {/* Frequency */}
                <div className="space-y-1">
                    <label htmlFor="edit-frequency" className="label-base flex items-center gap-1.5">
                        <RefreshCw className="w-4 h-4" />
                        Frequency <span className="text-status-overdue">*</span>
                    </label>
                    <select
                        id="edit-frequency"
                        name="frequency"
                        value={formData.frequency}
                        onChange={handleChange}
                        className={`input-base ${errors.frequency ? 'border-red-500 focus:ring-red-500' : ''}`}
                        disabled={isSubmitting}
                    >
                        {frequencyOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    {errors.frequency && (
                        <span className="text-xs text-red-500 mt-1">{errors.frequency}</span>
                    )}
                </div>

                {/* Description */}
                <div className="space-y-1">
                    <label htmlFor="edit-description" className="label-base flex items-center gap-1.5">
                        <StickyNote className="w-4 h-4" />
                        Description
                    </label>
                    <textarea
                        id="edit-description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className="input-base resize-none"
                        placeholder="Optional notes..."
                        rows={3}
                        disabled={isSubmitting}
                    />
                </div>

                {/* Is Active */}
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="edit-is_active"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleChange}
                        className="w-4 h-4 rounded border-surface-border accent-accent cursor-pointer"
                        disabled={isSubmitting}
                    />
                    <label htmlFor="edit-is_active" className="text-sm text-text-secondary cursor-pointer select-none">
                        {t(KEYS.REMINDER_FIELD_IS_ACTIVE)}
                    </label>
                </div>
            </form>
        </Modal>
    );
};

export default EditReminderModal;
